import httpRequest from "@/apis/axios-api";
import type { CodeResult } from "@/types/request";
import {
  type Message,
  type MessageView,
  type MessageConversation,
  type MessageConversationView,
  type PagedData,
  type SystemMessageAdminItem,
  type CreateConversationResponse,
  type AdminDirectSendResponse,
  type AdminSearchUser,
} from "@/types/message";
import { GetAvatarUrl } from "@/util/avatar.ts";
import { formatConversationTime, parseTimeString } from "@/util/time.ts";

// 说明：axios 实例（apis/axios-api.ts）的响应拦截器返回的是 `response.data`，
// 即后端统一结构 `{ code, data }`；该文件为了兼容尚未补齐类型的调用点，暂时把
// 调用签名保留为 `Promise<any>`。这里的接口不依赖那个 any：统一显式声明返回类型，
// 并借助 axios 的第二个泛型参数 R 给出真实结构
//   httpRequest<unknown, CodeResult<T>>(...) —— 无需类型断言，也不改变运行期行为。

export function ParseMessageConversation(item: MessageConversation): MessageConversationView {
  const result: MessageConversationView = {
    id: item.id,
    type: item.type,
    peerId: item.peer_id,
    peerUsername: item.peer_username,
    peerNickname: item.peer_nickname,
    peerEmail: item.peer_email,
    avatarUrl: "",
    lastMessage: item.last_message || "",
    lastMessageTime: "",
    lastMessageTimeMs: 0,
    unreadCount: item.unread_count || 0,
  };
  result.avatarUrl = GetAvatarUrl(result.peerUsername, result.peerEmail);
  if (item.last_message_time) {
    const d = parseTimeString(item.last_message_time);
    if (d) {
      result.lastMessageTime = formatConversationTime(d);
      result.lastMessageTimeMs = d.getTime();
    }
  }
  return result;
}

export function ParseMessage(item: Message, currentUserId: number): MessageView {
  let createTimeMs = 0;
  let createTime = "";
  if (item.create_time) {
    const d = new Date(item.create_time);
    createTimeMs = d.getTime();
    createTime = d.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" });
  }
  const result: MessageView = {
    id: item.id,
    conversationId: item.conversation_id,
    senderId: item.sender_id,
    senderUsername: item.sender_username,
    senderNickname: item.sender_nickname,
    senderEmail: item.sender_email,
    avatarUrl: "",
    content: item.content,
    type: item.type,
    isRead: item.is_read,
    readTime: item.read_time,
    isRecalled: item.is_recalled,
    createTime,
    createTimeMs,
    isSelf: item.sender_id === currentUserId,
  };
  result.avatarUrl = GetAvatarUrl(result.senderUsername, result.senderEmail);
  return result;
}

// Get conversation list
export function GetConversationList(): Promise<CodeResult<MessageConversation[]>> {
  return httpRequest<unknown, CodeResult<MessageConversation[]>>({
    url: "/message/conversation/list",
    method: "get",
  });
}

// Get total unread count (for badge)
export function GetUnreadCount(): Promise<CodeResult<number>> {
  return httpRequest<unknown, CodeResult<number>>({
    url: "/message/unread/count",
    method: "get",
  });
}

// Get messages in a conversation (paginated, loads latest)
export function GetMessageList(conversationId: number, beforeId: number = 0): Promise<CodeResult<Message[]>> {
  let url = `/message/list?conversation_id=${conversationId}`;
  if (beforeId > 0) {
    url += `&before_id=${beforeId}`;
  }
  return httpRequest<unknown, CodeResult<Message[]>>({
    url,
    method: "get",
  });
}

// Get new messages since a given ID (for polling)
export function GetNewMessages(conversationId: number, afterId: number): Promise<CodeResult<Message[]>> {
  return httpRequest<unknown, CodeResult<Message[]>>({
    url: `/message/new?conversation_id=${conversationId}&after_id=${afterId}`,
    method: "get",
  });
}

// Send a message（成功时 data 为新消息 id）
export function PostSendMessage(conversationId: number, content: string): Promise<CodeResult<number>> {
  return httpRequest<unknown, CodeResult<number>>({
    url: "/message/send",
    method: "post",
    data: {
      conversation_id: conversationId,
      content,
    },
  });
}

// Mark conversation as read（后端成功时不返回 data）
export function PostReadMessage(conversationId: number): Promise<CodeResult<null>> {
  return httpRequest<unknown, CodeResult<null>>({
    url: "/message/read",
    method: "post",
    data: {
      conversation_id: conversationId,
    },
  });
}

// Get or create conversation with a user by username
export function GetCreateConversation(username: string): Promise<CodeResult<CreateConversationResponse>> {
  return httpRequest<unknown, CodeResult<CreateConversationResponse>>({
    url: `/message/create?username=${encodeURIComponent(username)}`,
    method: "get",
  });
}

// Admin: Send a broadcast system message（成功时 data 为新公告 id）
export function PostSendSystemMessage(
  content: string,
  expireTime: string | null,
  hideBeforeRegister: boolean,
): Promise<CodeResult<number>> {
  return httpRequest<unknown, CodeResult<number>>({
    url: "/message/system/send",
    method: "post",
    data: {
      content,
      expire_time: expireTime,
      hide_before_register: hideBeforeRegister,
    },
  });
}

// Admin: Edit an existing broadcast system message（后端成功时不返回 data）
export function PostEditSystemMessage(
  id: number,
  content: string,
  expireTime: string | null,
  hideBeforeRegister: boolean,
): Promise<CodeResult<null>> {
  return httpRequest<unknown, CodeResult<null>>({
    url: "/message/system/edit",
    method: "post",
    data: {
      id,
      content,
      expire_time: expireTime,
      hide_before_register: hideBeforeRegister,
    },
  });
}

// Admin: Get system message broadcast history (paginated)
export function GetSystemMessageAdminList(page: number = 1): Promise<CodeResult<PagedData<SystemMessageAdminItem>>> {
  return httpRequest<unknown, CodeResult<PagedData<SystemMessageAdminItem>>>({
    url: `/message/system/admin/list?page=${page}`,
    method: "get",
  });
}

// Admin: Get system-direct conversation list (point-to-point messages)
export function GetAdminDirectConversationList(keyword: string = "", page: number = 1): Promise<CodeResult<PagedData<MessageConversation>>> {
  let url = `/message/admin/conversation/list?page=${page}`;
  if (keyword) {
    url += `&keyword=${encodeURIComponent(keyword)}`;
  }
  return httpRequest<unknown, CodeResult<PagedData<MessageConversation>>>({
    url,
    method: "get",
  });
}

// Admin: Send a direct system message to a specific user
export function PostAdminDirectSendMessage(username: string, content: string): Promise<CodeResult<AdminDirectSendResponse>> {
  return httpRequest<unknown, CodeResult<AdminDirectSendResponse>>({
    url: "/message/admin/direct/send",
    method: "post",
    data: { username, content },
  });
}

// Admin: Get messages in a system-direct conversation
export function GetAdminDirectMessages(conversationId: number, beforeId: number = 0): Promise<CodeResult<Message[]>> {
  let url = `/message/admin/messages?conversation_id=${conversationId}`;
  if (beforeId > 0) {
    url += `&before_id=${beforeId}`;
  }
  return httpRequest<unknown, CodeResult<Message[]>>({
    url,
    method: "get",
  });
}

// Admin: Search users by username/nickname prefix
export function GetAdminSearchUser(keyword: string): Promise<CodeResult<AdminSearchUser[]>> {
  return httpRequest<unknown, CodeResult<AdminSearchUser[]>>({
    url: `/message/admin/search/user?keyword=${encodeURIComponent(keyword)}`,
    method: "get",
  });
}
